/**
 * Creators directory — embedded section.
 *
 * Renders a v6-styled grid of MockCreator rows. Used on /feed to give
 * users a discovery surface for creators they could follow / subscribe
 * to. Tier chip + Free/Paid badge + Follow CTA per row.
 *
 * Pulls from lib/v6/mockCreators.ts for now (no real listCreators API
 * endpoint yet); swap the import for the live endpoint when wired.
 */
"use client";

import Link from "next/link";
import { CheckCircle2, Star, Crown } from "lucide-react";
import { MOCK_CREATORS, type MockCreator } from "@/lib/v6/mockCreators";

export function V6CreatorsDirectory({ limit = 6 }: { limit?: number }) {
  const creators = MOCK_CREATORS.slice(0, limit);

  return (
    <section className="mx-auto mt-6 w-full max-w-[1280px] px-4">
      <div className="mb-4 flex items-baseline justify-between gap-3">
        <div>
          <h2 className="text-[18px] font-bold tracking-tight sm:text-[22px]">
            Creators to follow
          </h2>
          <p
            className="mt-1 text-[12px]"
            style={{ color: "var(--color-text2)" }}
          >
            Subscribe to paid tiers, or follow free creators for daily
            market analysis + commentary.
          </p>
        </div>
        <Link
          href="/creators"
          className="flex-shrink-0 text-[12px] font-medium"
          style={{ color: "var(--color-accent)" }}
        >
          Browse all →
        </Link>
      </div>

      <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 lg:grid-cols-3">
        {creators.map((c) => (
          <CreatorTile key={c.username} creator={c} />
        ))}
      </div>
    </section>
  );
}

function CreatorTile({ creator }: { creator: MockCreator }) {
  const paid = creator.monthlyPriceCents > 0;
  return (
    <div
      className="flex flex-col gap-3 rounded-xl border p-4"
      style={{
        background: "var(--color-surface)",
        borderColor: "var(--color-border)",
      }}
    >
      <div className="flex items-start gap-3">
        <span
          aria-hidden="true"
          className="flex h-12 w-12 flex-shrink-0 items-center justify-center rounded-full text-[16px] font-bold"
          style={{
            background: "var(--color-surface2)",
            border: "1px solid var(--color-border)",
            color: "var(--color-text)",
          }}
        >
          {creator.displayName[0]?.toUpperCase()}
        </span>
        <div className="min-w-0 flex-1">
          <div className="flex items-center gap-1">
            <span className="truncate text-[14px] font-semibold">
              {creator.displayName}
            </span>
            {creator.verified && (
              <CheckCircle2
                size={14}
                strokeWidth={2}
                aria-label="Verified"
                style={{ color: "var(--color-accent)", flexShrink: 0 }}
              />
            )}
          </div>
          <div
            className="truncate text-[11px]"
            style={{ color: "var(--color-text3)" }}
          >
            @{creator.username} · {creator.region} · {creator.category}
          </div>
        </div>
        <span
          className="flex-shrink-0 rounded-full px-2 py-0.5 font-mono text-[10px] font-semibold uppercase tracking-[0.1em]"
          style={{
            background: paid
              ? "color-mix(in srgb, var(--color-accent) 14%, transparent)"
              : "var(--color-surface2)",
            color: paid ? "var(--color-accent)" : "var(--color-text3)",
          }}
        >
          {creator.tier === "VIP" && (
            <Crown size={10} className="-mt-0.5 mr-0.5 inline" />
          )}
          {creator.tier === "Premium" && (
            <Star size={10} className="-mt-0.5 mr-0.5 inline" />
          )}
          {creator.tier}
        </span>
      </div>
      <p
        className="line-clamp-2 text-[12px] leading-snug"
        style={{ color: "var(--color-text2)" }}
      >
        {creator.bio}
      </p>
      <div
        className="flex items-center justify-between gap-2 border-t pt-3"
        style={{ borderColor: "var(--color-border)" }}
      >
        <div
          className="font-mono text-[11px] font-medium"
          style={{ color: "var(--color-text3)" }}
        >
          {formatFollowers(creator.followers)} followers ·{" "}
          {creator.postsThisMonth} posts/mo
        </div>
        <Link
          href={`/u/${creator.username}`}
          className="rounded-md px-3 py-1.5 text-[12px] font-bold"
          style={{
            background: paid ? "var(--color-accent)" : "var(--color-text)",
            color: paid ? "#0a0a0a" : "var(--color-bg)",
          }}
        >
          {paid
            ? `Subscribe · $${(creator.monthlyPriceCents / 100).toFixed(2)}/mo`
            : "Follow"}
        </Link>
      </div>
    </div>
  );
}

function formatFollowers(n: number): string {
  if (n >= 1000) return `${(n / 1000).toFixed(1)}k`;
  return String(n);
}
